<template>
  <field
    :empty="isEmpty && !forceLabelToTop && !placeholder"
    v-bind="$attrs"
    :label="label"
    :icon="icon"
    class="text-left"
  >
    <input
      v-model="internalValue"
      v-maska="'#*:##:##'"
      v-bind="{ ...$attrs, class: { 'has-icon': !!$slots.icon } }"
      :placeholder="placeholder"
      :aria-label="label"
      type="text"
    >
    <template v-if="$slots.icon" #icon>
      <slot name="icon" />
    </template>
  </field>
</template>

<script lang="ts" setup>
import {
  watch, computed, ref, type FunctionalComponent,
} from 'vue';
import { useVModel, type VueInstance, watchDebounced } from '@vueuse/core';
import Field from '@components/utils/inputs/Field.vue';

interface Props {
  modelValue?: number | null;
  label?: string;
  placeholder?: string;
  icon?: VueInstance | FunctionalComponent | string | object;
  forceLabelToTop?: boolean;
}

const emit = defineEmits(['update:modelValue']);
const props = defineProps<Props>();
const value = useVModel(props, 'modelValue', emit, { passive: true });

const transformIn = function (val: number | null | undefined) {
  if (val) {
    const s = val % 60;
    const m = ((val % 3600) - s) / 60;
    const h = (val - m * 60 - s) / 3600;

    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  }
  return '';
};

const transformOut = function (val: string | undefined) {
  const arr = val?.split(':');
  let res = null;
  if (arr && arr?.length === 3 && arr[0].length >= 1 && arr[1].length === 2 && arr[2].length === 2) {
    res = Number(arr[0]) * 3600 + Number(arr[1]) * 60 + Number(arr[2]);
  }
  return res;
};

const internalValue = ref<string | undefined>(transformIn(value.value));

watch(value, (newExternalValue) => {
  if (newExternalValue) {
    const expression = transformIn(newExternalValue);
    if (internalValue.value !== expression) {
      internalValue.value = expression;
    }
  } else if (newExternalValue === undefined) {
    internalValue.value = undefined;
  }
});

watchDebounced(internalValue, (newValue) => {
  value.value = transformOut(newValue);
}, { flush: 'post', debounce: 250 });

const isEmpty = computed(() => {
  const currentValue = value.value;
  if (typeof currentValue === 'number') return !currentValue && currentValue !== 0;
  return !currentValue;
});
</script>
<style lang="scss" scoped>
input {
  background-color: var(--input-bg, var(--color-background-light));
  padding: var(--input-padding, #{px(10)} #{px(14)});
  outline: 0;

  &:disabled {
    @apply border-light-gray;
    @apply text-light-gray;
  }
  &:focus, &:focus-within {
    @apply border-dark;
    @apply text-neutral;
  }
}
</style>
